import React, { useMemo, useState } from "react";
import { formatPercent } from "../../utils/numbers/formatPercent";
import formatDollar from "../../_src_shared/utils/functions/formatDollar";
import { formatDateShort } from "../../utils/dates/formatDateShort";
import Flx from "../layout/Flx";
import {
  CheckBox,
  CheckBoxOutlineBlank,
  CheckCircleOutlineOutlined,
  InfoOutlined,
  RadioButtonUncheckedOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { Box, IconButton, Tooltip } from "@mui/material";
import Txt from "../typography/Txt";
import { isArray, isBoolean, isNil, isObject } from "lodash";
import { getUserFullNameFromEmail } from "../../utils/functions/getUserFullNameFromEmail";
import { useSelector } from "react-redux";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import { formatPhone } from "../../_src_shared/utils/functions/formatPhone";
import { formatAddressObject } from "../../_src_shared/utils/functions/formatAddressObject";

const OverviewItem = ({
  label,
  value,
  icon,
  type,
  labelTooltip,
  customValue,
  stacked = true,
  endContent,
  showIf,

  hide,
  sx,
}) => {
  const [showFullSSN, setShowFullSSN] = useState(false);
  const users = useSelector((state) => state.users);
  const formatSSN = (ssn) => {
    if (!ssn) return "N/A";
    const cleanSSN = ssn.toString().replace(/\D/g, "");
    if (cleanSSN.length !== 9) return ssn; // Return as-is if not 9 digits

    return `${cleanSSN.slice(0, 3)}-${cleanSSN.slice(3, 5)}-${cleanSSN.slice(5)}`;
  };

  const maskSSN = (ssn) => {
    if (!ssn) return "N/A";
    const cleanSSN = ssn.toString().replace(/\D/g, "");
    if (cleanSSN.length < 4) return ssn;

    const lastFour = cleanSSN.slice(-4);
    const maskedPart = "*".repeat(Math.max(0, cleanSSN.length - 4));

    // Always format as XXX-XX-XXXX for 9-digit SSNs
    if (cleanSSN.length === 9) {
      return `${maskedPart.slice(0, 3)}-${maskedPart.slice(3, 5)}-${lastFour}`;
    }
    return maskedPart + lastFour;
  };

  const renderedValue = useMemo(() => {
    if (customValue) {
      return customValue;
    }
    if (isNil(value)) {
      if (
        type === "checkbox" ||
        type === "booleanToggle" ||
        type === "booleanCheckbox"
      ) {
        return (
          <CheckBoxOutlineBlank
            // sx={{ fontSize: "18px !important" }}
            className="thin-8"
          />
        );
        // return <CheckBoxOutlineBlank  className="thin-8" />;
        // return <CheckBoxOutlineBlank  className="thin-8" />;
      }
      return "N/A";
    }

    if (isObject(value)) {
      if (type === "address") {
        return formatAddressObject(value);
      }
      return "Invalid Object";
    }

    if (isArray(value)) {
      return "Invalid List";
    }

    if (type) {
      if (type === "date") {
        return formatDateShort(value);
      }
      if (type === "dateTime") {
        return formatDateTime(value);
      }
      if (type === "dollar") {
        return formatDollar(value);
      }
      if (type === "percent") {
        return formatPercent(value);
      }
      if (type === "phone") {
        return formatPhone(value);
      }

      if (type === "user") {
        return getUserFullNameFromEmail(users, value);
      }
      if (type === "ssn") {
        return showFullSSN ? formatSSN(value) : maskSSN(value);
      }
      // if (type === "checklist") {
      //   return label;
      // }
      if (
        type === "checkbox" ||
        type === "booleanToggle" ||
        type === "booleanCheckbox"
      ) {
        if (isBoolean(value)) {
          if (value) {
            return <CheckBox className="thin-8" />;
          } else {
            return <CheckBoxOutlineBlank className="thin-8" />;
          }
        }
      }
    }
    return value;
  }, [value, type, showFullSSN, users, customValue]);

  const renderedIcon = useMemo(() => {
    if (icon) {
      return icon;
    }

    if (type === "checklist") {
      if (isBoolean(value)) {
        if (value) {
          return <CheckCircleOutlineOutlined className="thin-8" />;
        } else {
          return <RadioButtonUncheckedOutlined className="thin-8" />;
        }
      }
    }

    return null;
  }, [value, type, label]);

  const toggleSSNVisibility = () => {
    setShowFullSSN(!showFullSSN);
  };

  if (isBoolean(showIf) || isBoolean(hide)) {
    if (isBoolean(hide) && hide) {
      return null;
    }
    if (isBoolean(showIf) && !showIf) {
      return null;
    }
  }

  return (
    <Flx
      className={"overview-item"}
      gap={stacked ? 0.4 : 0.7}
      ac={!stacked}
      column={stacked}
      sx={{
        ".MuiSvgIcon-root": {
          // fontSize: "16px",
          fontSize: "19px",
        },
        ".txt": {
          fontSize: "12px",
          "&.type-checkbox": {
            lineHeight: 0,
          },
        },
        ...sx,
      }}
    >
      {renderedIcon}
      {label ? (
        labelTooltip ? (
          <Flx ac gap={0.4}>
            <Txt sx={{ color: "#33475b", fontSize: 12 }}>{label}</Txt>
            <Tooltip title={labelTooltip}>
              <InfoOutlined className="thin-9" />
            </Tooltip>
          </Flx>
        ) : (
          <Txt sx={{ color: "#33475b", fontSize: 12 }}>{label}</Txt>
        )
      ) : null}

      <Txt className={`type-${type}`} sx={{ fontWeight: 600 }}>
        {renderedValue}
        {type === "ssn" && (
          <IconButton
            size="small"
            onClick={toggleSSNVisibility}
            sx={{
              ml: 0.5,
              p: 0.25,
              "& .MuiSvgIcon-root": {
                fontSize: "14px",
              },
            }}
          >
            {showFullSSN ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        )}
      </Txt>
      {endContent && <Txt sx={{ opacity: 0.55, ml: -0.25 }}>{endContent}</Txt>}
    </Flx>
  );
};
export default OverviewItem;
